import { useState, useEffect, useRef, useCallback } from "react";
import strings from "./String";

const HEARTBEAT_INTERVAL_MS = 20000;
const HEARTBEAT_TIMEOUT_MS = 8000;
const FAILURES_TO_GO_OFFLINE = 2;

// Combines the browser online/offline events with an active heartbeat to the
// API's /health endpoint. The heartbeat is required because navigator.onLine
// only reflects adapter state — a Wi-Fi connection with no internet still
// reports "online".
export default function useConnectivity() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  const failureCountRef = useRef(0);
  const inFlightRef = useRef(false);
  const isOnlineRef = useRef(isOnline);

  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  const pingHealth = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HEARTBEAT_TIMEOUT_MS);

    let success = false;
    try {
      const res = await fetch(
        `${strings.pythiaApiBaseUrl}/PythiaReason/health`,
        { method: "GET", signal: controller.signal, cache: "no-store" }
      );
      success = res.ok;
    } catch (_e) {
      success = false;
    } finally {
      clearTimeout(timeoutId);
      inFlightRef.current = false;
    }

    if (success) {
      failureCountRef.current = 0;
      if (!isOnlineRef.current) setIsOnline(true);
    } else {
      failureCountRef.current += 1;
      if (
        failureCountRef.current >= FAILURES_TO_GO_OFFLINE &&
        isOnlineRef.current
      ) {
        setIsOnline(false);
      }
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      // Adapter is back, but verify reachability before lifting the modal.
      pingHealth();
    };
    const handleOffline = () => {
      failureCountRef.current = FAILURES_TO_GO_OFFLINE;
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // First check on mount — covers the case where the page loaded while
    // already disconnected from the internet but with adapter up.
    pingHealth();

    const intervalId = setInterval(pingHealth, HEARTBEAT_INTERVAL_MS);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(intervalId);
    };
  }, [pingHealth]);

  // Allow callers to report a network failure they observed (e.g. a streaming
  // fetch rejected mid-response). We re-verify immediately rather than wait
  // for the next interval.
  const reportNetworkFailure = useCallback(() => {
    failureCountRef.current = FAILURES_TO_GO_OFFLINE;
    setIsOnline(false);
    pingHealth();
  }, [pingHealth]);

  return { isOnline, reportNetworkFailure };
}
